import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, SortAsc, SortDesc, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import MovieCard from '@/components/ui/MovieCard';
import MovieModal from '@/components/ui/MovieModal';
import { cn } from '@/lib/utils';
import {
    fetchSeriesPopular,
    fetchSeriesTopRated
} from '@/services/tmdb';

const SeriesPage = () => {
    const [searchParams] = useSearchParams();
    const initialSort = searchParams.get('sort') || 'popular';

    const [series, setSeries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState(initialSort); // 'popular', 'top_rated'

    const [selectedSeries, setSelectedSeries] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Fetch Data
    useEffect(() => {
        const loadSeries = async () => {
            setIsLoading(true);
            try {
                let data;

                switch (sortBy) {
                    case 'top_rated':
                        data = await fetchSeriesTopRated(page);
                        break;
                    case 'popular':
                    default:
                        data = await fetchSeriesPopular(page);
                        break;
                }

                setSeries(data.results);
                setTotalPages(Math.min(data.totalPages, 500));
            } catch (error) {
                console.error("Error fetching series:", error);
            } finally {
                setIsLoading(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        };

        loadSeries();
    }, [page, sortBy]);

    // Handle showing details
    const handleShowDetails = useCallback((item) => {
        setSelectedSeries(item);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setTimeout(() => setSelectedSeries(null), 300);
    }, []);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const sortOptions = [
        { value: 'popular', label: 'Tendencia' },
        { value: 'top_rated', label: 'Mejor Valoradas' },
    ];

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisibleButtons = 5;
        let startPage = Math.max(1, page - Math.floor(maxVisibleButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

        if (endPage - startPage + 1 < maxVisibleButtons) {
            startPage = Math.max(1, endPage - maxVisibleButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <Button
                    key={i}
                    variant={i === page ? "default" : "outline"}
                    size="icon"
                    onClick={() => handlePageChange(i)}
                    className={cn(
                        "w-10 h-10 rounded-full",
                        i === page ? "bg-primary text-primary-foreground" : "bg-secondary text-gray-400 hover:text-white"
                    )}
                >
                    {i}
                </Button>
            );
        }
        return buttons;
    };

    return (
        <main className="min-h-screen bg-background pt-24 lg:pt-28 pb-12">
            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                            Series de TV
                        </h1>
                        <p className="text-muted-foreground">
                            Explora las mejores series
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="gap-2 bg-secondary/50 border-border hover:bg-secondary"
                                >
                                    {sortBy === 'top_rated' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                                    <span>
                                        {sortOptions.find((o) => o.value === sortBy)?.label}
                                    </span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-card border-border">
                                {sortOptions.map((option) => (
                                    <DropdownMenuItem
                                        key={option.value}
                                        onClick={() => {
                                            setSortBy(option.value);
                                            setPage(1);
                                        }}
                                        className={cn(
                                            'cursor-pointer',
                                            sortBy === option.value && 'bg-primary/10 text-primary'
                                        )}
                                    >
                                        {option.label}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                        <Loader2 className="w-12 h-12 text-primary animate-spin" />
                    </div>
                ) : series.length > 0 ? (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 md:gap-6 min-h-[500px]">
                            {series.map((item, index) => (
                                <MovieCard
                                    key={item.id}
                                    movie={item}
                                    onShowDetails={handleShowDetails}
                                    index={index}
                                />
                            ))}
                        </div>

                        <div className="flex justify-center items-center gap-2 mt-16 pt-8 border-t border-border/50">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>

                            <div className="flex gap-2">
                                {renderPaginationButtons()}
                            </div>

                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page === totalPages}
                                className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <h3 className="text-xl font-semibold text-foreground mb-2">
                            No se encontraron series
                        </h3>
                    </div>
                )}
            </div>

            <MovieModal
                movie={selectedSeries}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </main>
    );
};

export default SeriesPage;
